import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, mergeMap } from 'rxjs/operators';
import { BlogArticleDetail } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';

@Component({
  selector: 'libero-blog',
  templateUrl: './blog.component.html',
  styleUrls: ['./blog.component.scss'],
  standalone: false
})
export class BlogComponent implements OnInit, OnDestroy {
  private pageParamSubscription?: Subscription;
  private searchValueSubscription?: Subscription;
  public loadingPosts?: boolean;
  public articlesTotal?: number;
  private pageSize = 10;
  private searchPageSize = 100;
  public page = 1; // used for desktop
  private numOfPagesLoaded = 1; // used for phone
  public searchFormControl = new UntypedFormControl('');

  public searchMode = false;
  public searchedPosts: BlogArticleDetail[] = [];
  public articlePreviews: BlogArticleDetail[] = [];

  // Template-related constants
  public startPhoneOlderArticlesFromIndex = 3;
  public desktopSecondaryArticlesMaxIndex = 4;

  public constructor(
    public toastService: ToastNotificationService,
    private restApiService: RestApiService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  public ngOnDestroy(): void {
    this.pageParamSubscription?.unsubscribe();
    this.searchValueSubscription?.unsubscribe();
  }

  public ngOnInit(): void {
    this.pageParamSubscription = this.route.params.pipe(
      mergeMap((params) => {
        this.loadingPosts = true;
        this.page = params['page'] ? +params['page'] : 1;
        return this.restApiService.getBlogArticlesPage(
          { page: this.page - 1, size: this.pageSize})
      })
    ).subscribe({
      next: postsPage => {
        this.loadingPosts = false;
        this.articlePreviews = postsPage.content;
        this.articlesTotal = postsPage.totalCount;
      }
    });

    this.searchValueSubscription = this.searchFormControl.valueChanges.pipe(debounceTime(500))
    .subscribe(searchTerm => {
      if (!searchTerm) {
        this.resetSearch();
        return;
      }
      if (!this.searchMode) {
        this.searchMode = true;
      }
      this.restApiService.getBlogArticlesPage(
        { page: 0, size: this.searchPageSize})
      .subscribe(postsPage => this.searchedPosts = postsPage.content.filter(
        post => post.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
      ))
    })
  }

  public openDetail(id: number): void {
    this.router.navigate(['/blog/' + id]);
  }

  public goToPageDesktop(page: number): void {
    this.router.navigate(['/blog', { page }]);
  }

  public loadMoreForPhone(): void {
    // Invalid page check
    if (!this.allowLoadMore) {
      // Attempting to load another page even though all articles are fetched
      return;
    }
    this.numOfPagesLoaded++;
    this.restApiService.getBlogArticlesPage({ page: this.numOfPagesLoaded - 1, size: this.pageSize})
    .subscribe({
      next: postsPage => {
        this.loadingPosts = false;
        this.articlesTotal = postsPage.totalCount;
          this.articlePreviews = [...this.articlePreviews, ...postsPage.content];
      }
    });
  }

  public get allowLoadMore(): boolean {
    return !!this.articlesTotal &&
      Math.ceil(this.articlesTotal / this.pageSize) >
        this.numOfPagesLoaded
  }

  public resetSearch(): void {
    this.searchMode = false;
    this.searchFormControl.patchValue('');
    this.searchedPosts = [];
  }

}
